import { Info, AlertCircle, AlertTriangle, type LucideIcon } from 'lucide-react'
import type { AlertSeverity } from '@/types/alerts'

/**
 * Human-friendly severity language from the Hearth design system.
 * Never show LOW/MEDIUM/HIGH to users.
 */
export const severityLabel: Record<AlertSeverity, string> = {
  LOW: 'For your info',
  MEDIUM: 'Worth a look',
  HIGH: 'Needs attention',
  CRITICAL: 'Needs attention',
}

export const severityHeroTitle: Record<AlertSeverity, string> = {
  LOW: 'For your information',
  MEDIUM: 'Worth a look',
  HIGH: 'Needs your attention',
  CRITICAL: 'Needs your attention',
}

export const severityIcon: Record<AlertSeverity, LucideIcon> = {
  LOW: Info,
  MEDIUM: AlertCircle,
  HIGH: AlertTriangle,
  CRITICAL: AlertTriangle,
}

export interface SeverityTone {
  /** Tinted section/card background (50-step) */
  bg: string
  /** Hairline border (200-step) */
  border: string
  /** Foreground text/icon (600-step) */
  fg: string
  /** Solid dot / edge bar */
  dot: string
  /** Soft chip fill behind the foreground icon */
  chipBg: string
  /** Soft top-to-surface gradient wash for unread/attention cards */
  wash: string
}

const grayTone: SeverityTone = {
  bg: 'bg-gray-100',
  border: 'border-gray-200',
  fg: 'text-gray-600',
  dot: 'bg-gray-400',
  chipBg: 'bg-gray-500/10',
  wash: 'bg-gradient-to-b from-gray-100 to-surface',
}

const warningTone: SeverityTone = {
  bg: 'bg-warning-50',
  border: 'border-warning-200',
  fg: 'text-warning-600',
  dot: 'bg-warning-500',
  chipBg: 'bg-warning-600/10',
  wash: 'bg-gradient-to-b from-warning-50 to-surface',
}

const errorTone: SeverityTone = {
  bg: 'bg-error-50',
  border: 'border-error-200',
  fg: 'text-error-600',
  dot: 'bg-error-500',
  chipBg: 'bg-error-600/10',
  wash: 'bg-gradient-to-b from-error-50 to-surface',
}

export const severityTone: Record<AlertSeverity, SeverityTone> = {
  LOW: grayTone,
  MEDIUM: warningTone,
  HIGH: errorTone,
  CRITICAL: errorTone,
}

const severityRank: Record<AlertSeverity, number> = {
  LOW: 0,
  MEDIUM: 1,
  HIGH: 2,
  CRITICAL: 3,
}

export function highestSeverity(severities: AlertSeverity[]): AlertSeverity {
  return severities.reduce<AlertSeverity>(
    (max, s) => (severityRank[s] > severityRank[max] ? s : max),
    'LOW'
  )
}
